import * as React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { ACTIVE_SUPPORTED_CARRIERS } from "helpers/carriers";
import { Producer } from "../../../runQuote/components/intialInformation/Producer";
import { AgentEmail } from "../../../runQuote/components/intialInformation/AgentEmail";
import { PolicyEffectiveDate } from "../../../runQuote/components/intialInformation/PolicyEffectiveDate";
import { normalizeProducer } from "../../../runQuote/components/intialInformation/normalizeProducer";
import { InsuredState } from "../InsuredState";

const PERSONAL_QUOTE_CARRIER = ACTIVE_SUPPORTED_CARRIERS.find(
  (carrier) => carrier.id === "Progressive"
)?.id;

export function InitialInfo({ user, producers }) {
  const { control, setValue, getValues } = useFormContext();

  const allowedCarriers = useWatch({ control, name: "step1.allowedCarriers" });
  const carriers = useWatch({ control, name: "step1.carrier" });

  const [openPolicyDate, setOpenPolicyDate] = React.useState(false);

  const producerOptions = React.useMemo(() => {
    if (producers.loading || !producers.data?.users) return [];
    return producers.data.users.map((u) => ({
      value: String(u.id),
      label: u.name,
      email: u.email,
    }));
  }, [producers.loading, producers.data]);

  React.useEffect(() => {
    if (user.loading || !user.data || !producerOptions.length) return;

    const current = getValues("step1.producer");
    const normalized = normalizeProducer(current);

    if (current && current !== normalized) {
      setValue("step1.producer", normalized, {
        shouldDirty: false,
        shouldValidate: true,
      });
      return;
    }

    if (!normalized) {
      const match = producerOptions.find((u) => u.value === String(user.data.id));
      if (match) {
        setValue("step1.producer", normalizeProducer(match.value), {
          shouldDirty: false,
          shouldValidate: true,
        });
      }
    }
  }, [user.loading, user.data, producerOptions, setValue, getValues]);

  React.useEffect(() => {
    const d = getValues("step1.policyDate");
    if (!d) {
      setValue("step1.policyDate", new Date(), {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [getValues, setValue]);

  React.useEffect(() => {
    if (!Array.isArray(allowedCarriers) || !Array.isArray(carriers)) return;
    const nextCarriers =
      PERSONAL_QUOTE_CARRIER && allowedCarriers.includes(PERSONAL_QUOTE_CARRIER)
        ? [PERSONAL_QUOTE_CARRIER]
        : [];

    if (JSON.stringify(nextCarriers) !== JSON.stringify(carriers)) {
      setValue("step1.carrier", nextCarriers, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [allowedCarriers, carriers, setValue]);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold">Initial Information</h2>

      <Producer
        control={control}
        producers={producers}
        producerOptions={producerOptions}
      />

      <AgentEmail control={control} producers={producers} />

      <PolicyEffectiveDate control={control}
        openPolicyDate={openPolicyDate}
        setOpenPolicyDate={setOpenPolicyDate}
      />

      <InsuredState control={control} producers={producers} setValue={setValue} />
    </div>
  );
}
